/**
 * High-level vault create / unlock flows (Task #84).
 *
 * These functions combine KDF + AES-GCM + X25519 with the network API to:
 *   - mint a brand-new vault for a user (with both password and recovery wraps)
 *   - unlock an existing vault using either a password or the recovery phrase
 */

import { apiRequest } from "@/lib/queryClient";
import {
  ARGON2,
  aesGcmDecrypt,
  aesGcmEncrypt,
  b64ToBytes,
  bytesToB64,
  createNewVaultMaterial,
  deriveKeyArgon2id,
  randomBytes,
  type WrappedBlob,
} from "./crypto";
import {
  generateRecoveryPhrase,
  isValidRecoveryPhrase,
  normalizeRecoveryPhrase,
  recoveryPhraseToEntropy,
} from "./recoveryKey";
import { getMek, unlockVault } from "./vaultState";

// Server-side vault row shape we care about. Other fields are ignored.
export interface ServerVault {
  kdfAlgo: string;
  kdfSaltB64: string;
  kdfIterations: number;
  kdfMemoryKib: number;
  kdfParallelism: number;
  wrappedMekByPassword: WrappedBlob;
  wrappedMekByRecoveryKey: WrappedBlob;
  recoverySaltB64: string;
  wrappedX25519PrivateKey: WrappedBlob;
  publicX25519Key: string;
  vaultVersion: number;
}

// Recovery-phrase KDF is cheaper because the phrase itself is high-entropy.
const RECOVERY_KDF: { iterations: number; memoryKib: number } = { iterations: 2, memoryKib: 32 * 1024 };

async function deriveRecoveryKek(phrase: string, saltB64: string): Promise<Uint8Array> {
  const entropy = recoveryPhraseToEntropy(phrase);
  // Use the entropy bytes (not the words) as input — same shape regardless of
  // wordlist normalization differences.
  const pseudoPassword = bytesToB64(entropy);
  return deriveKeyArgon2id(pseudoPassword, saltB64, RECOVERY_KDF.iterations, RECOVERY_KDF.memoryKib, 1);
}

export interface CreateVaultResult {
  recoveryPhrase: string;
  publicKeyB64: string;
}

/**
 * Mint a brand-new vault on this device and persist it server-side. The
 * returned recovery phrase MUST be shown to the user exactly once.
 */
export async function createVault(password: string): Promise<CreateVaultResult> {
  if (!password || password.length < 8) {
    throw new Error("Password must be at least 8 characters.");
  }
  const recoveryPhrase = generateRecoveryPhrase();
  const { mek, identity } = await createNewVaultMaterial();

  const kdfSalt = randomBytes(16);
  const recoverySalt = randomBytes(16);
  const kdfSaltB64 = bytesToB64(kdfSalt);
  const recoverySaltB64 = bytesToB64(recoverySalt);

  const passwordKek = await deriveKeyArgon2id(password, kdfSaltB64);
  const recoveryKek = await deriveRecoveryKek(recoveryPhrase, recoverySaltB64);

  try {
    const wrappedMekByPassword = await aesGcmEncrypt(passwordKek, mek);
    const wrappedMekByRecoveryKey = await aesGcmEncrypt(recoveryKek, mek);
    const wrappedX25519PrivateKey = await aesGcmEncrypt(mek, identity.privateKey);

    const publicKeyB64 = bytesToB64(identity.publicKey);

    await apiRequest("PUT", "/api/vault", {
      kdfAlgo: "argon2id",
      kdfSaltB64,
      kdfIterations: ARGON2.iterations,
      kdfMemoryKib: ARGON2.memoryKib,
      kdfParallelism: ARGON2.parallelism,
      wrappedMekByPassword,
      wrappedMekByRecoveryKey,
      recoverySaltB64,
      wrappedX25519PrivateKey,
      publicX25519Key: publicKeyB64,
      vaultVersion: 1,
    });

    unlockVault(mek, identity);
    try {
      await apiRequest("POST", "/api/vault/unlocked", {});
    } catch {
      // non-fatal
    }

    return { recoveryPhrase, publicKeyB64 };
  } finally {
    // Always wipe local KEK copies, even on failure paths.
    passwordKek.fill(0);
    recoveryKek.fill(0);
  }
}

export class VaultUnlockError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VaultUnlockError";
  }
}

export async function fetchVault(): Promise<ServerVault | null> {
  try {
    const res = await apiRequest("GET", "/api/vault");
    return (await res.json()) as ServerVault;
  } catch (err: any) {
    // 404 → no vault yet
    if (typeof err?.message === "string" && err.message.startsWith("404")) return null;
    throw err;
  }
}

export async function unlockWithPassword(password: string): Promise<void> {
  const vault = await fetchVault();
  if (!vault) throw new VaultUnlockError("No vault exists yet on this account.");
  const kek = await deriveKeyArgon2id(
    password,
    vault.kdfSaltB64,
    vault.kdfIterations,
    vault.kdfMemoryKib,
    vault.kdfParallelism,
  );
  let mek: Uint8Array;
  try {
    mek = await aesGcmDecrypt(kek, vault.wrappedMekByPassword);
  } catch {
    kek.fill(0);
    throw new VaultUnlockError("Incorrect password.");
  }
  kek.fill(0);
  const priv = await aesGcmDecrypt(mek, vault.wrappedX25519PrivateKey);
  const pub = b64ToBytes(vault.publicX25519Key);
  unlockVault(mek, { privateKey: priv, publicKey: pub });
  try {
    await apiRequest("POST", "/api/vault/unlocked", {});
  } catch {
    // non-fatal
  }
}

/**
 * Set a new vault password. Requires that the vault is already unlocked in
 * memory (typically after a recovery-phrase unlock as the "forgot password"
 * flow). Re-derives a fresh password KEK, re-wraps the MEK, and persists
 * only the password-wrap fields server-side. The recovery phrase and identity
 * keypair are not affected.
 */
export async function setNewPassword(newPassword: string): Promise<void> {
  if (!newPassword || newPassword.length < 8) {
    throw new Error("Password must be at least 8 characters.");
  }
  const mek = getMek();
  if (!mek) {
    throw new Error("Vault must be unlocked before changing its password.");
  }
  const kdfSalt = randomBytes(16);
  const kdfSaltB64 = bytesToB64(kdfSalt);
  const passwordKek = await deriveKeyArgon2id(newPassword, kdfSaltB64);
  try {
    const wrappedMekByPassword = await aesGcmEncrypt(passwordKek, mek);
    await apiRequest("POST", "/api/vault/rotate-password", {
      kdfAlgo: "argon2id",
      kdfSaltB64,
      kdfIterations: ARGON2.iterations,
      kdfMemoryKib: ARGON2.memoryKib,
      kdfParallelism: ARGON2.parallelism,
      wrappedMekByPassword,
    });
  } finally {
    passwordKek.fill(0);
  }
}

export async function unlockWithRecoveryPhrase(phraseRaw: string): Promise<void> {
  const phrase = normalizeRecoveryPhrase(phraseRaw);
  if (!isValidRecoveryPhrase(phrase)) {
    throw new VaultUnlockError("That doesn't look like a valid 24-word recovery phrase.");
  }
  const vault = await fetchVault();
  if (!vault) throw new VaultUnlockError("No vault exists yet on this account.");
  const kek = await deriveRecoveryKek(phrase, vault.recoverySaltB64);
  let mek: Uint8Array;
  try {
    mek = await aesGcmDecrypt(kek, vault.wrappedMekByRecoveryKey);
  } catch {
    kek.fill(0);
    throw new VaultUnlockError("Recovery phrase did not unlock this vault.");
  }
  kek.fill(0);
  const priv = await aesGcmDecrypt(mek, vault.wrappedX25519PrivateKey);
  const pub = b64ToBytes(vault.publicX25519Key);
  unlockVault(mek, { privateKey: priv, publicKey: pub });
  try {
    await apiRequest("POST", "/api/vault/unlocked", {});
  } catch {
    // non-fatal
  }
}
